'use client';

import React, {
  Suspense,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { useRouter } from '@/shared/i18n';
import { useGetSpaceMeApi } from '@/features/my-space/api/get-space-me';
import { ProductBillingCycleType } from '@/shared/constants';
import { useGetProductPlansApi } from '@/shared/apis/product/get-product-plans';
import type { GetProductPlansResponseType } from '@/shared/apis/product/get-product-plans';
import { usePatchSubscriptionPlanScheduledChangeApi } from '@/shared/apis/subscribe/patch-subscription-free-plan-scheduled-change';
import { PaymentStartAction } from '@/shared/apis/payment/payment-start';
import { endAccountSwitchSession } from '@/shared/apis/auth/end-account-switch-session';
import { FloatingDebugAccountSwitch } from './FloatingDebugAccountSwitch';
import { FloatingDebugSpaceInfo } from './FloatingDebugSpaceInfo';
import { FloatingDebugPlanSwitcher } from './FloatingDebugPlanSwitcher';
import { FloatingDebugPointExpireSection } from './FloatingDebugPointExpireSection';
import { FloatingDebugSpaceSection } from './FloatingDebugSpaceSection';
import { DebugBugIcon } from './DebugBugIcon';
import Close from '@/shared/ui/generated-web-icons/Close';
import { ToolButton } from '@crabs-ai/ds-react/stories/primitives/buttons/tool-button/index';
import { usePostAdminAccountSwitchEndApi } from '@/shared/apis/admin/post-admin-account-switch-end';
import {
  canUseOperatorDebug,
  parseOperatorOverrideStorageValue,
  serializeOperatorOverride,
} from './operator-role-toggle';
import { DEBUG_STORAGE_KEYS } from './debug-storage';
import { registerOperatorDebugPanelOpener } from '@/shared/ui/debug/floating-debug-button/debug-panel-controller';
import { useOperatorDebugAccess } from '@/shared/hooks/use-operator-debug-access';

const STORAGE_KEYS = DEBUG_STORAGE_KEYS;

const FloatingDebugBasicControls = React.lazy(() =>
  import('./FloatingDebugBasicControls').then((module) => ({
    default: module.FloatingDebugBasicControls,
  })),
);

// 요소 크기 상수
const COLLAPSED_SIZE = 40; // 접힌 상태: 40px (h-10 w-10)
const EXPANDED_WIDTH = 320; // 펼친 상태: 320px (w-80)
const EXPANDED_MAX_HEIGHT = 0.8; // max-h-[80vh] = 80vh
const MARGIN = 16; // 여백: 16px
const MINIMIZED_RESERVED_TOP_AREA_HEIGHT = 224;
const DEBUG_LAUNCHER_Z_INDEX = 100001;

const isStoredCollapsedPositionSafe = (
  position: { x: number; y: number },
  viewport: { width: number; height: number },
): boolean => {
  const devtoolsLeft = viewport.width - COLLAPSED_SIZE - MARGIN;
  const devtoolsTop = viewport.height - COLLAPSED_SIZE - MARGIN;
  const overlapsBottomRightDevtools =
    position.x + COLLAPSED_SIZE > devtoolsLeft &&
    position.y + COLLAPSED_SIZE > devtoolsTop;

  return (
    position.y >= MINIMIZED_RESERVED_TOP_AREA_HEIGHT &&
    !overlapsBottomRightDevtools
  );
};

const getDefaultCollapsedPosition = (viewport: {
  width: number;
  height: number;
}): { x: number; y: number } => ({
  x: viewport.width - COLLAPSED_SIZE - MARGIN,
  // 로컬 TanStack Query Devtools 버튼 바로 위에 배치한다.
  y: Math.max(
    MINIMIZED_RESERVED_TOP_AREA_HEIGHT,
    viewport.height - COLLAPSED_SIZE * 2 - MARGIN * 2,
  ),
});

export function FloatingDebugButton() {
  const router = useRouter();
  const { mutateAsync: postSwitchEnd, isPending: isSwitchEndPending } =
    usePostAdminAccountSwitchEndApi();
  const {
    hasOperatorDebugAccess: originalHasOperatorRole,
    isOperatorDebugAccessLoading: isAccountLoading,
  } = useOperatorDebugAccess();
  const { data: spaceData } = useGetSpaceMeApi();
  const { mutateAsync: patchPlanChange, isPending: isProcessing } =
    usePatchSubscriptionPlanScheduledChangeApi();

  // Monthly 플랜 목록 조회
  const { data: monthlyPlans } = useGetProductPlansApi(
    {
      spaceId: spaceData?.data?.id,
      billingCycleType: ProductBillingCycleType.Monthly,
    },
    { enabled: !!spaceData?.data?.id },
  );

  // Annual 플랜 목록 조회
  const { data: annualPlans } = useGetProductPlansApi(
    {
      spaceId: spaceData?.data?.id,
      billingCycleType: ProductBillingCycleType.Annual,
    },
    { enabled: !!spaceData?.data?.id },
  );

  const [mounted, setMounted] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isMinimized, setIsMinimized] = useState(true);
  const [operatorOverride, setOperatorOverride] = useState<boolean | null>(
    null,
  );
  const [tenantOverride, setTenantOverride] = useState<number | null>(null);
  const [isFeaturesExpanded, setIsFeaturesExpanded] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] =
    useState<ProductBillingCycleType>(ProductBillingCycleType.Monthly);
  const [isPlanDropdownOpen, setIsPlanDropdownOpen] = useState(false);
  const [isChangingPlan, setIsChangingPlan] = useState(false);

  // 위치 및 드래그 상태
  // 초기 위치 계산 (클라이언트 사이드에서만)
  const getInitialPositionValue = (): { x: number; y: number } | null => {
    if (typeof window === 'undefined') {
      return null;
    }
    return getDefaultCollapsedPosition({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  };

  // 접힌 상태의 버튼 위치 (항상 유지)
  const [collapsedPosition, setCollapsedPosition] = useState<{
    x: number;
    y: number;
  } | null>(getInitialPositionValue());
  // 펼쳐진 상태의 패널 위치 (화면 안에 들어오도록 조정됨)
  const [expandedPosition, setExpandedPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const elementStartPos = useRef<{ x: number; y: number } | null>(null);
  const animationFrameId = useRef<number | null>(null);
  // 드래그 중 DOM 직접 조작을 위한 ref
  const dragElementRef = useRef<HTMLElement | null>(null);
  // 드래그가 실제로 발생했는지 추적 (클릭 이벤트 방지용)
  const hasDragged = useRef(false);

  // 패널 컨테이너 ref
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // 외부 클릭 감지 및 최소화
  useEffect(() => {
    // 최소화 상태일 때는 이벤트 리스너를 등록하지 않음
    if (isMinimized || !mounted) {
      return;
    }

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      // 드롭다운이 열려있을 때는 외부 클릭 감지를 무시
      if (isPlanDropdownOpen) {
        return;
      }

      if (!panelRef.current || !(event.target instanceof Node)) {
        return;
      }

      const target = event.target as HTMLElement;

      // 클릭된 요소가 패널 내부인지 확인
      const isInsidePanel = panelRef.current.contains(target);

      // Radix UI 포털(드롭다운, 다이얼로그 등) 내부인지 확인
      // closest()로 클릭 대상의 조상 중 포털/다이얼로그가 있는지 검사
      const isInsideRadixPortal = target.closest(
        '[data-radix-popper-content-wrapper], [data-radix-portal], [role="dialog"], [role="alertdialog"], [data-slot="dialog-overlay"]',
      );

      // 패널 외부이고 Radix 포털 내부도 아닌 경우에만 최소화
      if (!isInsidePanel && !isInsideRadixPortal) {
        setIsMinimized(true);
        setIsCollapsed(true);
      }
    };

    // 클릭 및 터치 이벤트 리스너 등록
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    // cleanup: 이벤트 리스너 제거
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMinimized, mounted, isPlanDropdownOpen]);

  // 화면 경계 내로 위치 제한하는 함수
  const constrainPosition = useCallback(
    (x: number, y: number, isExpanded: boolean): { x: number; y: number } => {
      const width = isExpanded ? EXPANDED_WIDTH : COLLAPSED_SIZE;
      const height = isExpanded
        ? Math.min(window.innerHeight * EXPANDED_MAX_HEIGHT, window.innerHeight)
        : COLLAPSED_SIZE;

      // 화면 경계 내로 제한
      const constrainedX = Math.max(
        MARGIN,
        Math.min(x, window.innerWidth - width - MARGIN),
      );
      const constrainedY = Math.max(
        MARGIN,
        Math.min(y, window.innerHeight - height - MARGIN),
      );

      return { x: constrainedX, y: constrainedY };
    },
    [],
  );

  // 초기 위치 계산 (bottom-4 right-4 기준)
  const getInitialPosition = useCallback((): { x: number; y: number } => {
    return getDefaultCollapsedPosition({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, []);

  // 초기 상태 로드
  useEffect(() => {
    // mounted를 먼저 true로 설정
    setMounted(true);

    // 접힘 상태 복원
    const savedCollapsed = localStorage.getItem(STORAGE_KEYS.COLLAPSED);
    if (savedCollapsed !== null) {
      setIsCollapsed(savedCollapsed === 'true');
    }

    // 최소화 상태 복원
    const savedMinimized = localStorage.getItem(STORAGE_KEYS.MINIMIZED);
    if (savedMinimized !== null) {
      setIsMinimized(savedMinimized === 'true');
    }

    // operator override 상태 복원
    const savedOperatorOverride = localStorage.getItem(
      STORAGE_KEYS.OPERATOR_OVERRIDE,
    );
    if (savedOperatorOverride !== null) {
      setOperatorOverride(
        parseOperatorOverrideStorageValue(savedOperatorOverride),
      );
    }

    // tenant override 상태 복원
    const savedTenantOverride = localStorage.getItem(
      STORAGE_KEYS.TENANT_OVERRIDE,
    );
    if (savedTenantOverride !== null) {
      // 레거시 boolean 값 호환: 'true' → 2 (GS Retail), 'false' → null
      if (savedTenantOverride === 'true') {
        setTenantOverride(2);
      } else if (savedTenantOverride === 'false') {
        setTenantOverride(null);
      } else {
        const parsed = Number(savedTenantOverride);
        setTenantOverride(isNaN(parsed) || parsed === 0 ? null : parsed);
      }
    }

    // 위치 복원 - window 객체가 있는지 확인 (SSR 안전)
    if (typeof window !== 'undefined') {
      const savedPosition = localStorage.getItem(STORAGE_KEYS.POSITION);
      if (savedPosition) {
        try {
          const parsed = JSON.parse(savedPosition);
          if (
            parsed &&
            typeof parsed.x === 'number' &&
            typeof parsed.y === 'number'
          ) {
            // 복원된 위치가 화면 경계와 주요 상단 UI 밖에 있는지 확인
            const constrained = constrainPosition(parsed.x, parsed.y, false);
            if (
              isStoredCollapsedPositionSafe(constrained, {
                width: window.innerWidth,
                height: window.innerHeight,
              })
            ) {
              setCollapsedPosition(constrained);
              return; // 위치 설정 완료
            }
          }
        } catch {
          // 파싱 실패 시 초기 위치 사용
        }
      }
      // 저장된 위치가 없거나 파싱 실패 시 초기 위치 사용
      const initialPos = getInitialPosition();
      setCollapsedPosition(initialPos);
    }
  }, [constrainPosition, getInitialPosition]);

  // 접힘 상태 저장
  useEffect(() => {
    if (mounted) {
      localStorage.setItem(STORAGE_KEYS.COLLAPSED, isCollapsed.toString());
    }
  }, [isCollapsed, mounted]);

  // 최소화 상태 저장
  useEffect(() => {
    if (mounted) {
      localStorage.setItem(STORAGE_KEYS.MINIMIZED, isMinimized.toString());
    }
  }, [isMinimized, mounted]);

  // 접힌 상태 위치 저장
  useEffect(() => {
    if (mounted && collapsedPosition) {
      localStorage.setItem(
        STORAGE_KEYS.POSITION,
        JSON.stringify(collapsedPosition),
      );
    }
  }, [collapsedPosition, mounted]);

  // 화면 크기 변경 시 위치 자동 조정
  useEffect(() => {
    if (!mounted) {
      return;
    }

    const handleResize = () => {
      // debounce를 위해 requestAnimationFrame 사용
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }

      animationFrameId.current = requestAnimationFrame(() => {
        if (collapsedPosition) {
          const constrained = constrainPosition(
            collapsedPosition.x,
            collapsedPosition.y,
            false,
          );
          setCollapsedPosition(constrained);
        }
        if (expandedPosition) {
          const constrained = constrainPosition(
            expandedPosition.x,
            expandedPosition.y,
            true,
          );
          setExpandedPosition(constrained);
        }
      });
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [mounted, collapsedPosition, expandedPosition, constrainPosition]);

  // 드래그 시작 (접힌 상태에서만)
  const handleDragStart = useCallback(
    (clientX: number, clientY: number, element: HTMLElement) => {
      if (!isMinimized || !isCollapsed || !collapsedPosition) {
        return;
      }

      setIsDragging(true);
      hasDragged.current = false; // 드래그 시작 시 초기화
      dragStartPos.current = { x: clientX, y: clientY };
      elementStartPos.current = { ...collapsedPosition };
      dragElementRef.current = element;
      // transition 제거하여 즉시 반응
      element.style.transition = 'none';
    },
    [isMinimized, isCollapsed, collapsedPosition],
  );

  // 드래그 중 - DOM 직접 조작으로 즉시 반응
  const handleDragMove = useCallback(
    (clientX: number, clientY: number) => {
      if (
        !isDragging ||
        !dragStartPos.current ||
        !elementStartPos.current ||
        !dragElementRef.current
      ) {
        return;
      }

      const deltaX = clientX - dragStartPos.current.x;
      const deltaY = clientY - dragStartPos.current.y;

      // 최소 이동 거리 체크 (드래그로 간주)
      const minDragDistance = 5;
      if (
        Math.abs(deltaX) > minDragDistance ||
        Math.abs(deltaY) > minDragDistance
      ) {
        hasDragged.current = true;
      }

      const newX = elementStartPos.current.x + deltaX;
      const newY = elementStartPos.current.y + deltaY;

      // 화면 경계 내로 제한
      const constrained = constrainPosition(newX, newY, false);

      // DOM 직접 조작으로 즉시 업데이트
      dragElementRef.current.style.left = `${constrained.x}px`;
      dragElementRef.current.style.top = `${constrained.y}px`;
    },
    [isDragging, constrainPosition],
  );

  // 드래그 종료
  const handleDragEnd = useCallback(() => {
    if (!isDragging || !dragElementRef.current) {
      return;
    }

    // DOM에서 현재 위치 읽기
    const currentLeft = parseFloat(
      dragElementRef.current.style.left.replace('px', ''),
    );
    const currentTop = parseFloat(
      dragElementRef.current.style.top.replace('px', ''),
    );

    if (!isNaN(currentLeft) && !isNaN(currentTop)) {
      const constrained = constrainPosition(currentLeft, currentTop, false);
      setCollapsedPosition(constrained);
    }

    // transition 복원
    dragElementRef.current.style.transition = '';
    dragElementRef.current = null;

    setIsDragging(false);
    dragStartPos.current = null;
    elementStartPos.current = null;

    // 다음 클릭 이벤트를 위해 약간의 지연 후 hasDragged 초기화
    // (마우스업 후 클릭 이벤트가 발생하기 전에 처리)
    setTimeout(() => {
      hasDragged.current = false;
    }, 0);
  }, [isDragging, constrainPosition]);

  // 마우스 드래그 이벤트
  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      handleDragMove(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      handleDragEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // 터치 드래그 이벤트
  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (touch) {
        e.preventDefault();
        handleDragMove(touch.clientX, touch.clientY);
      }
    };

    const handleTouchEnd = () => {
      handleDragEnd();
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // 터치 이벤트 핸들러 (모바일)
  const handleTouchStart = (e: React.TouchEvent) => {
    // 버튼 클릭이 아닌 경우에만 처리
    if ((e.target as HTMLElement).tagName !== 'BUTTON') {
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // 버튼 클릭이 아닌 경우에만 처리
    if ((e.target as HTMLElement).tagName !== 'BUTTON') {
      e.preventDefault();
    }
  };

  // 모바일에서 close 버튼 터치 이벤트 핸들러
  const handleCloseTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation(); // 이벤트 버블링 방지
    handleToggleMinimize();
  };

  // operator 토글 (null = 서버 원본값 사용, false = operator 강제 OFF)
  const handleOperatorOverrideChange = (next: boolean | null) => {
    setOperatorOverride(next);
    const serializedNext = serializeOperatorOverride(next);

    if (serializedNext === null) {
      localStorage.removeItem(STORAGE_KEYS.OPERATOR_OVERRIDE);
    } else {
      localStorage.setItem(STORAGE_KEYS.OPERATOR_OVERRIDE, serializedNext);
    }
    // 페이지 새로고침
    window.location.reload();
  };

  // tenant override 변경 (null = 서버 원본값 사용, 양수 = 특정 테넌트 override)
  const handleTenantOverrideChange = (next: number | null) => {
    setTenantOverride(next);
    if (next === null) {
      localStorage.removeItem(STORAGE_KEYS.TENANT_OVERRIDE);
    } else {
      localStorage.setItem(STORAGE_KEYS.TENANT_OVERRIDE, String(next));
    }
    // 페이지 새로고침
    window.location.reload();
  };

  // 충분한 공간이 있는 쪽으로 위치 조정
  const adjustPositionForExpansion = useCallback(
    (currentPos: { x: number; y: number }) => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const panelWidth = EXPANDED_WIDTH;
      const panelMaxHeight = viewportHeight * EXPANDED_MAX_HEIGHT;

      // 현재 위치 기준으로 좌/우 공간 확인
      const spaceOnLeft = currentPos.x;
      const spaceOnRight = viewportWidth - currentPos.x - COLLAPSED_SIZE;

      let newX = currentPos.x;
      let newY = currentPos.y;

      // 오른쪽 공간이 부족하면 왼쪽으로 이동
      if (spaceOnRight < panelWidth + MARGIN) {
        // 왼쪽 공간이 더 넓으면 왼쪽으로 이동
        if (spaceOnLeft > spaceOnRight) {
          newX = currentPos.x - panelWidth + COLLAPSED_SIZE;
        } else {
          // 오른쪽 공간이 부족하지만 왼쪽도 부족하면 가능한 만큼 왼쪽으로
          newX = viewportWidth - panelWidth - MARGIN;
        }
      }

      // 상/하 공간 확인
      const spaceOnTop = currentPos.y;
      const spaceOnBottom = viewportHeight - currentPos.y - COLLAPSED_SIZE;

      // 아래 공간이 부족하면 위로 이동
      if (spaceOnBottom < panelMaxHeight + MARGIN) {
        if (spaceOnTop > spaceOnBottom) {
          newY = currentPos.y - panelMaxHeight + COLLAPSED_SIZE;
        } else {
          newY = viewportHeight - panelMaxHeight - MARGIN;
        }
      }

      // 화면 경계 내로 제한
      return constrainPosition(newX, newY, true);
    },
    [constrainPosition],
  );

  // Space 전환/새로고침으로 열린 상태가 복원돼도 expandedPosition은 메모리에 없으므로
  // 접힌 버튼 좌표를 그대로 쓰면 패널 대부분이 화면 오른쪽 밖으로 밀린다.
  useEffect(() => {
    if (!mounted || isMinimized || !collapsedPosition) return;
    setExpandedPosition(adjustPositionForExpansion(collapsedPosition));
  }, [mounted, isMinimized, collapsedPosition, adjustPositionForExpansion]);

  const handleOpenDebugPanel = useCallback(() => {
    if (collapsedPosition) {
      const adjustedPos = adjustPositionForExpansion(collapsedPosition);
      setExpandedPosition(adjustedPos);
    }
    setIsMinimized(false);
    setIsCollapsed(false);
  }, [collapsedPosition, adjustPositionForExpansion]);

  useEffect(
    () => registerOperatorDebugPanelOpener(handleOpenDebugPanel),
    [handleOpenDebugPanel],
  );

  // 최소화/펼치기 토글
  const handleToggleMinimize = useCallback(() => {
    if (isMinimized) {
      handleOpenDebugPanel();
    } else {
      // 펼쳐진 상태에서 접을 때: 접힌 버튼을 원래 위치에 표시 (위치 변경 없음)
      setIsMinimized(true);
      setIsCollapsed(true);
    }
  }, [isMinimized, handleOpenDebugPanel]);

  // 플랜 변경 핸들러
  const handlePlanChange = async (plan: GetProductPlansResponseType) => {
    if (!spaceData?.data?.id || isChangingPlan || isProcessing) {
      return;
    }

    // Free 플랜으로 변경하는 경우에는 페이지 이동 없이 처리
    const isFreePlan = plan.name.toLowerCase().includes('free');

    setIsChangingPlan(true);

    try {
      const response = await patchPlanChange({
        spaceId: spaceData.data.id,
        body: {
          productId: plan.id,
          // Free 플랜은 가격이 0이므로 checksum 을 0 으로 고정
          checksum: isFreePlan ? 0 : plan.customerAmount || 0,
        },
      });

      if (isFreePlan) {
        // Free 플랜 변경은 페이지 이동 없이 새로고침만 수행
        if (response.code === 200) {
          window.location.reload();
        }
      } else {
        if (response.code === 200 && response.data) {
          // response.data 는 PaymentStartResponse 객체다. 예전엔 이 객체를 그대로
          // orderId 자리에 넣어 `?orderId=[object Object]` 로 이동했고, 결과 화면이
          // 없는 주문을 폴링해 404(결제 정보를 찾을 수 없습니다)를 냈다.
          const { actionType, orderId, checkoutUrl } = response.data;

          if (actionType === PaymentStartAction.TOSS_WIDGET) {
            // 이 디버그 도구는 Toss 위젯을 띄우지 않는다. 위젯 없이 성공 화면으로 보내면
            // 미완료 주문을 폴링하게 되므로, 정상 결제 화면으로 유도하고 끝낸다.
            console.warn(
              '빌링키가 없어 Toss 위젯이 필요하다. 디버그 전환 대신 결제 화면에서 진행하라.',
            );
            router.push('/payment/plan/product');
          } else if (
            actionType === PaymentStartAction.REDIRECT &&
            checkoutUrl
          ) {
            // 외부 결제(Lemon) 는 체크아웃을 거쳐야 주문이 확정된다.
            window.location.assign(checkoutUrl);
          } else if (actionType === PaymentStartAction.RESERVED) {
            // 즉시 과금 없이 다음 갱신 예약. 주문이 없으므로 orderId 를 쓰지 않는다.
            router.push(`/payment/plan/change/success?planId=${plan.id}`);
          } else {
            router.push(`/payment/plan/subscribe/success?orderId=${orderId}`);
          }
        } else {
          // 실패: 실패 페이지로 이동
          router.push('/payment/plan/subscribe/fail');
        }
      }
    } catch (error) {
      // 실패 시 유료 플랜의 경우에만 실패 페이지로 이동
      console.error('플랜 즉시 변경 실패:', error);
      if (!isFreePlan) {
        router.push('/payment/plan/subscribe/fail');
      }
    } finally {
      setIsChangingPlan(false);
    }
  };

  // 계정전환 복귀 핸들러
  const handleSwitchEnd = useCallback(async () => {
    try {
      await endAccountSwitchSession(postSwitchEnd);
    } catch {
      // network/5xx는 switch credential을 보존하여 다시 시도할 수 있게 한다.
    }
  }, [postSwitchEnd]);

  // 하이드레이션 전이거나 로딩 중이면 렌더링하지 않음
  if (!mounted || isAccountLoading) {
    return null;
  }

  // 실제 operator 권한이 없는 계정에서는 디버그 패널을 노출하지 않음.
  //
  // 정책: 프로덕션에서도 `hasOperatorRole=true` 계정은 디버그 패널 사용이 허용된다.
  // (tenant override, coin override, plan switcher, guide toggle 등 운영 진단 목적)
  // 따라서 `NEXT_PUBLIC_APP_ENV` 기반 환경 가드는 두지 않는다.
  // 클라이언트 응답 위조로 우회 가능한 체크이므로 실제 권한이 필요한 쓰기 작업은
  // 서버 측에서 별도 검증해야 한다.
  if (!canUseOperatorDebug(originalHasOperatorRole)) {
    return null;
  }

  // 접힌 상태 위치가 없으면 렌더링하지 않음
  if (!collapsedPosition) {
    return null;
  }

  // 최소화 상태: 작은 버튼만 표시 (접힌 상태에서만 드래그 가능)
  if (isMinimized) {
    return (
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          // 실제 포인터 이동이 발생했을 때만 클릭 이벤트 방지
          if (hasDragged.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          handleToggleMinimize();
        }}
        onMouseDown={(e) => {
          if (isCollapsed && buttonRef.current) {
            handleDragStart(e.clientX, e.clientY, buttonRef.current);
          }
        }}
        onTouchStart={(e) => {
          handleTouchStart(e);
          const touch = e.touches[0];
          if (isCollapsed && touch && buttonRef.current) {
            handleDragStart(touch.clientX, touch.clientY, buttonRef.current);
          }
        }}
        onTouchEnd={handleTouchEnd}
        style={{
          position: 'fixed',
          left: `${collapsedPosition.x}px`,
          top: `${collapsedPosition.y}px`,
          zIndex: DEBUG_LAUNCHER_Z_INDEX,
          cursor: isCollapsed ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
        }}
        className={`flex h-10 w-10 items-center justify-center rounded-full border border-sys-cont-primary-default bg-sys-bg-surface-primary shadow-lg transition-all ${
          isDragging ? '' : 'hover:scale-110 active:scale-95'
        }`}
        aria-label="디버그 패널 열기"
      >
        <DebugBugIcon className="size-md text-sys-cont-neutral-default" />
      </button>
    );
  }

  // 펼쳐진 상태: 전체 패널 표시 (접힌 버튼 위치 기준으로 조정된 위치 사용)
  const displayPosition = expandedPosition || collapsedPosition;
  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        left: `${displayPosition.x}px`,
        top: `${displayPosition.y}px`,
      }}
      className="z-[9999] flex max-h-[80vh] w-80 flex-col gap-3xs overflow-y-auto rounded-xs bg-sys-bg-surface-primary p-sm shadow-lg"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2xs text-sys-cont-neutral-default">
          <DebugBugIcon className="size-sm" />
          <span>Debug</span>
        </div>
        <ToolButton
          onClick={handleToggleMinimize}
          onTouchEnd={handleCloseTouchEnd}
          aria-label="디버그 패널 최소화"
        >
          <Close />
        </ToolButton>
      </div>

      {!isCollapsed && (
        <div className="flex flex-col gap-2">
          <Suspense fallback={null}>
            <FloatingDebugBasicControls
              originalHasOperatorRole={originalHasOperatorRole}
              operatorOverride={operatorOverride}
              onOperatorToggle={handleOperatorOverrideChange}
              tenantOverride={tenantOverride}
              onTenantToggle={handleTenantOverrideChange}
            />
          </Suspense>

          {/* 구분선 */}
          <div className="my-1 border-t border-gray-300 dark:border-gray-600" />

          {/* 계정전환 섹션 */}
          <FloatingDebugAccountSwitch
            onSwitchEnd={handleSwitchEnd}
            isSwitchEndPending={isSwitchEndPending}
          />

          {/* 구분선 */}
          <div className="my-1 border-t border-gray-300 dark:border-gray-600" />

          {/* Space 정보 */}
          <FloatingDebugSpaceInfo
            spaceData={spaceData}
            isFeaturesExpanded={isFeaturesExpanded}
            onToggleFeatures={() =>
              setIsFeaturesExpanded(
                (prevIsFeaturesExpanded) => !prevIsFeaturesExpanded,
              )
            }
          />

          {/* 구분선 */}
          <div className="my-1 border-t border-gray-300 dark:border-gray-600" />

          {/* 플랜 변경 섹션 */}
          {spaceData?.data && (
            <FloatingDebugPlanSwitcher
              spaceId={spaceData.data.id}
              currentPlanId={spaceData.data.planId}
              monthlyPlans={monthlyPlans?.data}
              annualPlans={annualPlans?.data}
              selectedBillingCycle={selectedBillingCycle}
              isPlanDropdownOpen={isPlanDropdownOpen}
              isChangingPlan={isChangingPlan}
              isProcessing={isProcessing}
              onChangeBillingCycle={setSelectedBillingCycle}
              onOpenChange={setIsPlanDropdownOpen}
              onSelectPlan={handlePlanChange}
            />
          )}

          {/* 구분선 */}
          <div className="my-1 border-t border-gray-300 dark:border-gray-600" />

          {/* 포인트 만료 섹션 */}
          <FloatingDebugPointExpireSection />

          {/* 구분선 */}
          <div className="my-1 border-t border-gray-300 dark:border-gray-600" />

          {/* Space 전환 섹션 */}
          <FloatingDebugSpaceSection />
        </div>
      )}
    </div>
  );
}
